import axios from 'axios';
import QueryString from 'qs';
import { z } from 'zod';

import { apiRoutes, StripeCollectionDataZodSchema } from './config';

const strapiUrl = process.env.NEXT_PUBLIC_STRAPI_API_URL;

export type BlogType = 'CANDIDATE' | 'AICOMPANY' | 'PRESSROOM' | 'CONTECH';

export const mediaDataSchema = z.object({
  url: z
    .string()
    .transform((url) =>
      /^https?:\/\//.test(url) ? url : `${strapiUrl}${url}`
    ),
  placeholder: z.string().nullable(),
  width: z.number(),
  height: z.number(),
});
export type MediaDataStripe = z.infer<typeof mediaDataSchema>;

const tagSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  tag: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  publishedAt: z.string(),
});
export type BlogTag = z.infer<typeof tagSchema>;

const tagsRelationSchema = z.array(tagSchema);

const blogsBaseFields = {
  createdAt: z.string(),
  documentId: z.string(),
  publishedAt: z.string(),
  title: z.string(),
  standardImage: mediaDataSchema,
  bannerImage: mediaDataSchema,
  slug: z.string(),
  seoDescription: z.string().nullish(),
};

const blogPostFields = {
  id: z.number(),
  documentId: z.string(),
  title: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  publishedAt: z.string(),
  standardImage: mediaDataSchema,
  bannerImage: mediaDataSchema,
  slug: z.string(),
  content: z.string(),
  seoTitle: z.string(),
  seoDescription: z.string(),
};

const withConTechTags = <Shape extends z.ZodRawShape>(shape: Shape) =>
  z
    .object({ ...shape, con_tech_tags: tagsRelationSchema.optional() })
    .transform(({ con_tech_tags: conTechTags, ...fields }) => ({
      ...fields,
      tags: conTechTags ?? [],
    }));

const blogsBaseSchema = z.object({
  ...blogsBaseFields,
  tags: tagsRelationSchema,
});

const blogPostSchema = z.object({
  ...blogPostFields,
  tags: tagsRelationSchema,
});

const blogTypeApiConfig = {
  CANDIDATE: {
    root: apiRoutes.candidatesBlogs._root,
    tagsRoot: apiRoutes.tags._root,
    tagsField: 'tags',
    listSchema: blogsBaseSchema,
    postSchema: blogPostSchema,
  },
  AICOMPANY: {
    root: apiRoutes.aiCompanies._root,
    tagsRoot: apiRoutes.tags._root,
    tagsField: 'tags',
    listSchema: blogsBaseSchema,
    postSchema: blogPostSchema,
  },
  PRESSROOM: {
    root: apiRoutes.pressRoom._root,
    tagsRoot: apiRoutes.tags._root,
    tagsField: 'tags',
    listSchema: blogsBaseSchema,
    postSchema: blogPostSchema,
  },
  CONTECH: {
    root: apiRoutes.conTechBlogs._root,
    tagsRoot: apiRoutes.conTechTags._root,
    tagsField: 'con_tech_tags',
    listSchema: withConTechTags(blogsBaseFields),
    postSchema: withConTechTags(blogPostFields),
  },
} satisfies Record<
  BlogType,
  {
    root: string;
    tagsRoot: string;
    tagsField: string;
    listSchema: z.ZodTypeAny;
    postSchema: z.ZodTypeAny;
  }
>;

const paramsSerializer = {
  serialize: (params: Record<string, unknown>) =>
    QueryString.stringify(params, { encodeValuesOnly: true }),
};

const buildSearchFilter = (search?: string) => {
  const term = search?.trim();
  if (!term) return undefined;
  return {
    $or: [
      { title: { $containsi: term } },
      { seoDescription: { $containsi: term } },
    ],
  };
};

export const getBlogBaseDataList = async ({
  offset = 0,
  pageSize = 20,
  search,
  slugToOmit,
  tagDocumentId,
  blogType,
}: {
  offset?: number;
  pageSize?: number;
  search?: string;
  slugToOmit?: string;
  tagDocumentId?: string;
  blogType: BlogType;
}) => {
  const { root, tagsField, listSchema } = blogTypeApiConfig[blogType];

  const { data } = await axios.get(root, {
    params: {
      fields: [
        'title',
        'documentId',
        'createdAt',
        'publishedAt',
        'slug',
        'seoDescription',
      ],
      populate: ['standardImage', 'bannerImage', tagsField],
      pagination: {
        start: offset,
        limit: pageSize,
      },
      filters: {
        ...buildSearchFilter(search),
        slug: {
          $ne: slugToOmit,
        },
        ...(tagDocumentId && {
          [tagsField]: { documentId: { $eq: tagDocumentId } },
        }),
      },
      sort: ['createdAt:desc'],
    },
    paramsSerializer,
  });

  return StripeCollectionDataZodSchema(listSchema).parse(data);
};

export const getBlogPost = async ({
  slug,
  blogType,
}: {
  slug: string;
  blogType: BlogType;
}) => {
  const { root, tagsField, postSchema } = blogTypeApiConfig[blogType];

  const { data } = await axios.get(root, {
    params: {
      populate: ['bannerImage', 'standardImage', tagsField],
      filters: {
        slug: {
          $eq: slug,
        },
      },
    },
    paramsSerializer,
  });

  return StripeCollectionDataZodSchema(postSchema).parse(data);
};

export const getBlogTags = async ({ blogType }: { blogType: BlogType }) => {
  const { tagsRoot } = blogTypeApiConfig[blogType];

  const { data } = await axios.get(tagsRoot, {
    params: {
      sort: ['tag:asc'],
      pagination: { limit: 200 },
    },
    paramsSerializer,
  });

  return StripeCollectionDataZodSchema(tagSchema).parse(data);
};
